import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {useSortedCategories} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {Text, TextSize} from '@Components/text';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import type {CategoryPill, EmailComponentTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import styles from './admin-save-section-template-panel.module.scss';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {useAppDispatch} from '@/hooks';
import {addTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.reducer';

export interface AdminSaveSectionTemplatePanelProps {
  sectionHtmlContent: string;
  sectionJsonContent: string;
  htmlMediaQueries: string;
}

export function AdminSaveSectionTemplatePanel({sectionHtmlContent, sectionJsonContent, htmlMediaQueries}: AdminSaveSectionTemplatePanelProps): ReactElement {
  const allCategories = useSortedCategories();
  const [selectedCategory, setSelectedCategory] = useState<CategoryPill | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSucessMessage, setShowSucessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dispatch = useAppDispatch();

  const getSelector = (): ReactElement => {
    return (
      <Button
        size={Size.XSMALL}
        type={Type.SECONDARY}
        icon="icon-caret-down"
        iconPlacement={IconPlacement.RIGHT}
        text={selectedCategory ? selectedCategory.name : window.i18next.t('pmwjs_menu_categories')}
        disabled={isLoading}
      />
    );
  };

  const getCategories = (): ReactElement => {
    return (
      <ul>
        {allCategories.map((category) => (
          <ControlledListItemType3
            id={category.id.toString()}
            text={category.name}
            isSelected={category.id === selectedCategory?.id}
            onClick={() => {
              setSelectedCategory(category);
            }}
            listSize={ControlledListItemSize.SMALL}
          />
        ))}
      </ul>
    );
  };

  const saveSectionTemplate = (): void => {
    setIsLoading(true);

    window.PMW.writeLocal('emailcomponenttemplate/createTemplate', {
      htmlContent: sectionHtmlContent,
      jsonContent: sectionJsonContent,
      mediaQueries: htmlMediaQueries,
      categoryId: selectedCategory?.id,
    })
      .then((response: EmailComponentTemplate) => {
        setShowSucessMessage(true);
        dispatch(addTemplate(response));
      })
      .catch((reason: Error) => {
        setShowErrorMessage(true);
        setErrorMessage(`Failed saving section: ${reason.message}`);
      });
  };

  const showCategorySelector = (): ReactElement | null => {
    return !showSucessMessage && !showErrorMessage ? (
      <>
        <div className="flex-row-justify-between spacing-m-b-6">
          <Text val={window.i18next.t('pmwjs_select_a_category_colon')} />
          <Dropdown alignment={ALIGNMENT_TYPE.BOTTOM_START} selector={getSelector()} popup={getCategories()} position={DROPDOWN_POSITION.FIXED} popUpHasCustomWidth />
        </div>
        <Button
          customClasses={'_full-width'}
          size={Size.XSMALL}
          type={Type.SECONDARY}
          text={window.i18next.t('pmwjs_save')}
          disabled={selectedCategory === undefined}
          onClick={saveSectionTemplate}
          isLoading={isLoading}
        />
      </>
    ) : null;
  };

  const showSavedStatus = (): ReactElement | null => {
    return showSucessMessage || showErrorMessage ? (
      <div
        className={`${styles.savingStatusContainer} ${showErrorMessage ? styles.savingFailed : styles.savingSuccessful} flex-row-justify-center flex-row-align-center spacing-p-5`}
      >
        <Icon icon={showErrorMessage ? 'icon-exclamation-triangle' : 'icon-check'} size={IconSize.SIZE_ICON_16} className={'spacing-m-r-4'} />
        <Text val={showErrorMessage ? errorMessage : window.i18next.t('pmwjs_saved_successfully')} size={TextSize.SMALL} />
      </div>
    ) : null;
  };

  return (
    <div>
      {showCategorySelector()}
      {showSavedStatus()}
    </div>
  );
}
